/**
 * requestInfoLinkResources.ts
 *
 * What the Request-Info Links list and its editor share: the resource kinds a
 * link can point at, how to load the pickable ones, and the list's query key.
 */
import type { ComboboxOption } from '@/components/ui';
import { formatCurrency } from '@/lib/utils';
import type { RequestInfoResourceType } from '@/services/marketing.service';
import { listAllProducts } from '@/services/products.service';
import { getPackageTemplates } from '@/services/packages.service';
import { listTemplates as listMembershipTemplates } from '@/services/memberships.service';
import { appointmentsService } from '@/services/appointments.service';

export const REQUEST_INFO_LINKS_QUERY_KEY = ['marketing', 'request-info-links'] as const;

/** The Templates page on its Request-Info Links tab — where the builder's back arrow goes. */
export const REQUEST_INFO_LINKS_TAB = '/marketing/templates?tab=links';

/** The builder page for a new link (`new`) or an existing one (its id). */
export function requestInfoLinkBuilderPath(id: number | 'new'): string {
  return `/marketing/templates/links/${id}`;
}

/** The API stores at most 200 characters; the textarea stops at the same place. */
export const NOTE_MAX = 200;

export const RESOURCE_KINDS: Array<{
  value: RequestInfoResourceType;
  label: string;
  noun: string;
  plural: string;
}> = [
  { value: 'product', label: 'Product', noun: 'product', plural: 'products' },
  { value: 'package', label: 'Package', noun: 'package', plural: 'packages' },
  { value: 'appointmentType', label: 'Appointment type', noun: 'appointment type', plural: 'appointment types' },
  { value: 'membership', label: 'Membership', noun: 'membership', plural: 'memberships' },
];

export const KIND_LABEL: Record<RequestInfoResourceType, string> = {
  product: 'Product',
  package: 'Package',
  appointmentType: 'Appointment type',
  membership: 'Membership',
};

/** Absent reads as active (older payloads); only an explicit 0/false is inactive. */
function isActiveFlag(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  return value === true || value === 1 || value === '1';
}

function priceMeta(price: unknown): string | undefined {
  const n = Number(price);
  return Number.isFinite(n) && n > 0 ? formatCurrency(n) : undefined;
}

function byLabel(a: ComboboxOption<number>, b: ComboboxOption<number>): number {
  return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
}

/**
 * The pickable resources of one kind, from the same service calls the rest of the
 * app lists them with. Inactive ones are left out: a link to something the
 * practice no longer offers is not something to start a campaign with.
 */
export async function loadResourceOptions(kind: RequestInfoResourceType): Promise<ComboboxOption<number>[]> {
  switch (kind) {
    case 'product': {
      const rows = (await listAllProducts()) ?? [];
      return rows
        .filter((p) => isActiveFlag(p.active))
        .map((p) => ({
          value: p.quoteItemCostID,
          label: p.strItem || `Product #${p.quoteItemCostID}`,
          meta: priceMeta(p.curRate),
        }))
        .sort(byLabel);
    }
    case 'package': {
      const rows = (await getPackageTemplates()) ?? [];
      return rows
        .filter((p) => isActiveFlag(p.active))
        .map((p) => ({ value: p.packageId, label: p.name || `Package #${p.packageId}`, meta: priceMeta(p.price) }))
        .sort(byLabel);
    }
    case 'membership': {
      const rows = (await listMembershipTemplates({ incrementType: 1, limit: 500 })) ?? [];
      return rows
        .filter((t) => isActiveFlag(t.active))
        .map((t) => ({ value: t.templateId, label: t.title || `Membership #${t.templateId}`, meta: priceMeta(t.price) }))
        .sort(byLabel);
    }
    case 'appointmentType':
    default: {
      // Already active-only on the API side.
      const rows = (await appointmentsService.getAppointmentTypes()) ?? [];
      return rows
        .map((t) => ({
          value: t.appointmentTypeId,
          label: t.appointmentTypeName || `Appointment type #${t.appointmentTypeId}`,
        }))
        .sort(byLabel);
    }
  }
}
